import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import {
  DEFAULT_PROMPT_WORKFLOW,
  isPromptWorkflowKey,
  type PromptWorkflowKey,
} from "../../shared/promptContracts/registry.ts";
import { useRoleAuth } from "../auth/AuthGate.tsx";
import { clearStoredAuth } from "../lib/auth.ts";
import { notificationCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";

type OperatorAuth = {
  role: "operator";
  token: string;
};

export type PromptTemplate = {
  key: string;
  workflow: PromptWorkflowKey;
  title: string;
  purpose: string;
  editableLabel: string;
  body: string;
  defaultBody: string;
  isCustom: boolean;
  updatedAt: number | null;
  updatedByRole: "operator" | "moderator" | null;
  userPromptSections: Array<{
    label: string;
    template: string;
    runtimeInputs: string[];
  }>;
  runtimeInputs: string[];
  outputSchema: string[];
  providerOptions: Array<{ label: string; value: string }>;
  requestPreview: string;
  requestPreviews?: Array<{
    label: string;
    description: string;
    candidateCount: number;
    schemaMaxItems: number;
    requestPreview: string;
  }>;
  requiredAdditions?: Array<{
    id: string;
    label: string;
    body: string;
    reason: string;
  }>;
};

type Drafts = Record<string, string>;
type PendingAction = { key: string; type: "save" | "reset" } | null;

export type PromptAdminContext = {
  auth: OperatorAuth;
  templates: PromptTemplate[];
  selectedTemplate: PromptTemplate;
  selectedDraft: string;
  selectedDirty: boolean;
  pending: PendingAction;
  setDraft: (key: string, body: string) => void;
  save: (template: PromptTemplate) => Promise<void>;
  reset: (template: PromptTemplate) => Promise<void>;
};

export function PromptAdminShell() {
  const auth = useRoleAuth("operator") as OperatorAuth;
  const navigate = useNavigate();
  const { promptSlug } = useParams();
  const templates = useQuery(convexApi.promptTemplates.list, { auth }) as PromptTemplate[] | undefined;
  const saveTemplate = useMutation(convexApi.promptTemplates.save);
  const resetTemplate = useMutation(convexApi.promptTemplates.reset);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [pending, setPending] = useState<PendingAction>(null);

  useEffect(() => {
    if (!templates?.length) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const template of templates) {
        if (next[template.key] === undefined) next[template.key] = template.body;
      }
      return next;
    });
  }, [templates]);

  useEffect(() => {
    if (!templates?.length) return;
    if (!promptSlug || !isPromptWorkflowKey(promptSlug)) {
      navigate(`/admin/llm-settings/${DEFAULT_PROMPT_WORKFLOW}`, { replace: true });
      return;
    }
    if (!templates.some((template) => template.workflow === promptSlug)) {
      navigate(`/admin/llm-settings/${DEFAULT_PROMPT_WORKFLOW}`, { replace: true });
    }
  }, [navigate, promptSlug, templates]);

  const selectedTemplate = useMemo(() => {
    if (!templates?.length) return null;
    if (promptSlug && isPromptWorkflowKey(promptSlug)) {
      return templates.find((template) => template.workflow === promptSlug) ?? templates[0];
    }
    return templates.find((template) => template.workflow === DEFAULT_PROMPT_WORKFLOW) ?? templates[0];
  }, [promptSlug, templates]);

  if (!templates) {
    return <div className="loading">Loading prompt templates...</div>;
  }

  if (!selectedTemplate) {
    return <div className="loading">No prompt templates are configured.</div>;
  }

  const selectedDraft = drafts[selectedTemplate.key] ?? selectedTemplate.body;
  const selectedDirty = selectedDraft !== selectedTemplate.body;

  function setDraft(key: string, body: string) {
    setDrafts((current) => ({ ...current, [key]: body }));
  }

  async function save(template: PromptTemplate) {
    const body = drafts[template.key] ?? template.body;
    setPending({ key: template.key, type: "save" });
    try {
      const updated = await saveTemplate({ auth, key: template.key, body }) as PromptTemplate;
      setDrafts((current) => ({ ...current, [template.key]: updated.body }));
      notify.success(notificationCopy.admin.promptSaved);
    } catch {
      notify.error(notificationCopy.admin.couldNotSavePrompt);
    } finally {
      setPending(null);
    }
  }

  async function reset(template: PromptTemplate) {
    setPending({ key: template.key, type: "reset" });
    try {
      const updated = await resetTemplate({ auth, key: template.key }) as PromptTemplate;
      setDrafts((current) => ({ ...current, [template.key]: updated.body }));
      notify.success(notificationCopy.admin.promptReset);
    } catch {
      notify.error(notificationCopy.admin.couldNotResetPrompt);
    } finally {
      setPending(null);
    }
  }

  const context: PromptAdminContext = {
    auth,
    templates,
    selectedTemplate,
    selectedDraft,
    selectedDirty,
    pending,
    setDraft,
    save,
    reset,
  };

  return (
    <div className="operator prompt-admin">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">LLM Settings</span>
          </div>
        </div>
        <div className="top-meta">
          <span className="pill on" data-testid="prompt-template-count">{templates.length} templates</span>
        </div>
        <div className="top-actions">
          <button
            className="btn ghost"
            data-testid="prompt-signout"
            type="button"
            onClick={() => {
              clearStoredAuth();
              window.location.assign("/");
            }}
          >
            Sign out
          </button>
          <Link className="btn ghost sm" data-testid="back-to-operator" to="/admin">
            <ArrowLeft aria-hidden="true" weight="bold" />
            Admin View
          </Link>
          <Link className="btn ghost sm" data-testid="open-sessions" to="/admin/sessions">
            Sessions
          </Link>
        </div>
      </header>

      <main className="prompt-admin-main">
        <aside className="prompt-template-list" aria-label="LLM workflow templates">
          <div className="prompt-admin-heading">
            <h1>LLM Settings</h1>
            <p>Admin-managed system instructions for LLM-backed workflows.</p>
          </div>
          {templates.map((template) => {
            const draft = drafts[template.key] ?? template.body;
            const dirty = draft !== template.body;
            return (
              <NavLink
                key={template.key}
                className={({ isActive }) => `prompt-template-tab ${isActive ? "selected" : ""}`}
                data-testid="prompt-template-tab"
                data-key={template.key}
                to={`/admin/llm-settings/${template.workflow}`}
              >
                <span className="prompt-template-title">{template.title}</span>
                <span className="prompt-template-meta">
                  {template.isCustom ? "custom" : "default"}
                  {dirty ? " / unsaved" : ""}
                </span>
              </NavLink>
            );
          })}
        </aside>

        <section className="prompt-editor-shell">
          <nav className="prompt-admin-tabs" aria-label="Prompt workspace">
            <NavLink
              data-testid="prompt-template-view-tab"
              end
              to={`/admin/llm-settings/${selectedTemplate.workflow}`}
            >
              Template
            </NavLink>
            <NavLink
              data-testid="prompt-replay-view-tab"
              to={`/admin/llm-settings/${selectedTemplate.workflow}/replay`}
            >
              Replay Lab
            </NavLink>
            <NavLink
              data-testid="prompt-diagnostics-view-tab"
              to={`/admin/llm-settings/diagnostics?workflow=${selectedTemplate.workflow}`}
            >
              Diagnostics
            </NavLink>
          </nav>
          <Outlet context={context} />
        </section>
      </main>
    </div>
  );
}

export function usePromptAdminContext() {
  return useOutletContext<PromptAdminContext>();
}
